import { Link, useParams } from 'react-router';
import {
  EmptyState,
  EVENT_STATES,
  LoadingRegion,
  Status,
  formatInZone,
  presentation,
} from '@gatecheckpro/design-system';
import { usableOrgCapabilities, type OrgCapability } from '@gatecheckpro/contracts';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';
import { useViewer } from '../lib/session.tsx';

interface EventSummary {
  readonly eventId: string;
  readonly slug: string;
  readonly title: string;
  readonly status: string;
  readonly timezone: string;
  readonly publishedAt: string | null;
  readonly createdAt: string;
  readonly venueName: string | null;
}

/**
 * The organizer's events.
 *
 * Draft and public events sit in one list rather than separate tabs: an
 * organizer's mental model is "my shows", and the status badge is what
 * distinguishes them.
 */
export function EventsPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const viewer = useViewer();
  const { resource } = useResource<{ events: EventSummary[] }>(
    organizationId === undefined
      ? null
      : `/api/v1/organizer/organizations/${organizationId}/events`,
  );

  const held = (viewer.organizations.find(
    (candidate) => candidate.organizationId === organizationId,
  )?.capabilities ?? []) as OrgCapability[];
  const canManage = usableOrgCapabilities(held, {
    mfaSatisfied: viewer.mfa.satisfiedForSession,
  }).includes('EVENT_MANAGE');

  if (resource.status === 'loading') return <LoadingRegion label="Loading events" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  const events = resource.data.events;

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-row gc-row--between">
        <div className="gc-stack gc-stack--tight">
          <h2>Events</h2>
          <p className="gc-muted">Everything this organization is putting on, public or not.</p>
        </div>
        {canManage && (
          <Link to="new" className="gc-button gc-button--primary">
            Create an event
          </Link>
        )}
      </div>

      {events.length === 0 ? (
        <EmptyState
          title="No events yet"
          action={
            canManage ? (
              <Link to="new" className="gc-button gc-button--primary">
                Create your first event
              </Link>
            ) : undefined
          }
        >
          An event is where sessions, gates, zones, ticket products and capacity all come together.
          Nothing is public until you publish it.
        </EmptyState>
      ) : (
        <div className="gc-grid">
          {events.map((event) => {
            const state = presentation(EVENT_STATES, event.status);
            return (
              <Link key={event.eventId} to={event.eventId} className="gc-event-card">
                <div className="gc-row gc-row--between">
                  <Status
                    tone={state.tone}
                    glyph={state.glyph}
                    label={state.label}
                    title={state.meaning}
                  />
                </div>
                <span className="gc-event-card__title">{event.title}</span>
                <span className="gc-muted">
                  {event.venueName ?? 'No venue yet'} · {event.timezone}
                </span>
                <span className="gc-muted">
                  {event.publishedAt === null
                    ? `Created ${formatInZone(event.createdAt, event.timezone)}`
                    : `Public since ${formatInZone(event.publishedAt, event.timezone)}`}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
